import React, { useState, useEffect } from 'react';
import { useAuth, axiosInstance as axios } from '../../context/AuthContext';
import { useFamilyTheme } from '../../context/FamilyThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Palette, Globe, Check, RotateCcw, X, ChevronDown, Download, FileJson, FileArchive, Users, Calendar, Image } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

// Color input component
const ColorPicker = ({ label, value, onChange, testId }) => (
  <div className="flex flex-col gap-2">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200 hover:border-gray-400 transition"
        data-testid={testId}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono uppercase"
        placeholder="#000000"
      />
    </div>
  </div>
);

// Data Export Card Component
function DataExportCard({ familyId, familyName }) {
  const [exportPreview, setExportPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchExportPreview();
  }, [familyId]);

  const fetchExportPreview = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/export/families/${familyId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExportPreview(response.data);
    } catch (error) {
      console.error('Error fetching export preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/api/export/families/${familyId}?format=${format}&include_photos=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'zip' ? 'application/zip' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const safeName = (familyName || 'family').replace(/[^a-zA-Z0-9 -_]/g, '').substring(0, 30);
      const date = new Date().toISOString().split('T')[0];
      link.download = `${safeName}_export_${date}.${format}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Family data exported successfully as ${format.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="bg-white">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
            <Download className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl">Export Family Data</CardTitle>
            <CardDescription>
              Download your family's data including members, events, and photos
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Data Preview */}
        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading preview...</div>
        ) : exportPreview ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">
                {exportPreview.export_preview.members + exportPreview.export_preview.proxy_members}
              </p>
              <p className="text-sm text-blue-600">Members</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">
                {exportPreview.export_preview.events}
              </p>
              <p className="text-sm text-purple-600">Events</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <Image className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">
                {exportPreview.export_preview.photos}
              </p>
              <p className="text-sm text-green-600">Photos</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <FileArchive className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-amber-700">
                {exportPreview.estimated_size.with_photos}
              </p>
              <p className="text-sm text-amber-600">Est. Size</p>
            </div>
          </div>
        ) : null}

        {/* What's Included */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-700 mb-2">What's included:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" /> Family members and their roles
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" /> Events, reunions, and milestones
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" /> Photos and albums
            </li>
            <li className="flex items-center gap-2 text-gray-400">
              <X className="w-4 h-4" /> Legal Vault documents (download separately for security)
            </li>
          </ul>
        </div>

        {/* Export Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => handleExport('json')}
            disabled={exporting}
            variant="outline"
            className="flex-1"
            data-testid="export-json-btn"
          >
            <FileJson className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export as JSON'}
          </Button>
          <Button
            onClick={() => handleExport('zip')}
            disabled={exporting}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="export-zip-btn"
          >
            <FileArchive className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export as ZIP (with photos)'}
          </Button>
        </div>

        <p className="text-xs text-gray-500 text-center">
          Your data belongs to you. Export anytime for backup or portability.
        </p>
      </CardContent>
    </Card>
  );
}

export function SettingsTab({ familyId, family, isOwner, onFamilyUpdate, onClose }) {
  const { updateColors: updateGlobalColors } = useFamilyTheme();
  const [presets, setPresets] = useState(null);
  const [byContinent, setByContinent] = useState({});
  const [selectedPreset, setSelectedPreset] = useState('');
  const [colors, setColors] = useState({
    primary: '#3B82F6',
    secondary: '#8B5CF6',
    tertiary: '',
    origin_country: '',
    origin_continent: ''
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load presets and current colors
  useEffect(() => {
    fetchPresets();
    if (family?.ancestral_colors) {
      setColors({
        primary: family.ancestral_colors.primary || '#3B82F6',
        secondary: family.ancestral_colors.secondary || '#8B5CF6',
        tertiary: family.ancestral_colors.tertiary || '',
        origin_country: family.ancestral_colors.origin_country || '',
        origin_continent: family.ancestral_colors.origin_continent || ''
      });
      if (family.ancestral_colors.origin_country) {
        setSelectedPreset(family.ancestral_colors.origin_country);
      }
    }
  }, [family]);

  const fetchPresets = async () => {
    try {
      const response = await axios.get(`${API}/api/ancestral-colors/presets`);
      setPresets(response.data.presets);
      setByContinent(response.data.by_continent);
    } catch (error) {
      console.error('Error fetching presets:', error);
    }
  };

  const handlePresetSelect = (presetKey) => {
    if (presetKey && presets[presetKey]) {
      const preset = presets[presetKey];
      setSelectedPreset(presetKey);
      setColors({
        primary: preset.colors[0],
        secondary: preset.colors[1],
        tertiary: preset.colors[2] || '',
        origin_country: presetKey,
        origin_continent: preset.continent
      });
      setHasChanges(true);
    }
  };

  const handleColorChange = (field, value) => {
    setColors(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    // Clear preset selection when manually changing colors
    if (['primary', 'secondary', 'tertiary'].includes(field)) {
      setSelectedPreset('custom');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/families/${familyId}/ancestral-colors`, colors);
      toast.success('Ancestral colors updated! Your family theme has been customized.');
      setHasChanges(false);
      
      // Update global theme immediately
      updateGlobalColors(colors);
      
      if (onFamilyUpdate) onFamilyUpdate();
    } catch (error) {
      toast.error('Failed to save colors');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setColors({
      primary: '#3B82F6',
      secondary: '#8B5CF6',
      tertiary: '',
      origin_country: '',
      origin_continent: ''
    });
    setSelectedPreset('');
    setHasChanges(true);
  };

  if (!isOwner) {
    return (
      <div className="space-y-6">
        <Card className="bg-white">
          <CardContent className="p-8 text-center">
            <Palette className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">Family Colors</h3>
            <p className="text-gray-500 mt-2">Only the family owner can customize ancestral colors.</p>
          </CardContent>
        </Card>
        
        {/* All members can export data */}
        <DataExportCard familyId={familyId} familyName={family?.name} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Ancestral Colors Card */}
      <Card className="bg-white">
        <CardHeader className="relative">
          {/* Close Button - Positioned closer to content */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-full transition"
              title="Close"
              data-testid="close-colors-btn"
            >
              <X className="w-6 h-6" strokeWidth={2.5} />
            </button>
          )}
          <div className="flex items-center gap-3 pr-12">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Palette className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl">Ancestral Colors</CardTitle>
              <CardDescription>
                Customize your family's theme with colors representing your heritage
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Preview Banner */}
          <div 
            className="rounded-xl p-6 text-white relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 50%${colors.tertiary ? `, ${colors.tertiary} 100%` : ', ' + colors.primary + ' 100%'})`
            }}
          >
            <div className="relative z-10">
              <h3 className="text-2xl font-bold">{family?.name || 'Your Family'}</h3>
              <p className="text-white/80 mt-1">Preview of your family theme</p>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
          </div>

          {/* Preset Selection */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Globe className="w-4 h-4" />
              Choose by Country/Region
            </label>
            <Select value={selectedPreset} onValueChange={handlePresetSelect}>
              <SelectTrigger 
                className="w-full max-w-sm h-10 border-2 border-gray-400 rounded-md bg-white hover:border-gray-500 focus:border-purple-500" 
                data-testid="country-preset-select"
              >
                <SelectValue placeholder="Select a country or region..." />
              </SelectTrigger>
              <SelectContent className="max-h-80 bg-white">
                {Object.entries(byContinent).map(([continent, countries]) => (
                  <div key={continent}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                      {continent}
                    </div>
                    {countries.map((country) => (
                      <SelectItem key={country.key} value={country.key}>
                        <div className="flex items-center gap-3">
                          <div className="flex gap-0.5">
                            {country.colors.map((c, i) => (
                              <div
                                key={i}
                                className="w-4 h-4 rounded-sm"
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                          <span>{country.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Or divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-white text-sm text-gray-500">or customize manually</span>
            </div>
          </div>

          {/* Manual Color Pickers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ColorPicker
              label="Primary Color"
              value={colors.primary}
              onChange={(v) => handleColorChange('primary', v)}
              testId="primary-color-picker"
            />
            <ColorPicker
              label="Secondary Color"
              value={colors.secondary}
              onChange={(v) => handleColorChange('secondary', v)}
              testId="secondary-color-picker"
            />
            <ColorPicker
              label="Tertiary Color (Optional)"
              value={colors.tertiary || '#FFFFFF'}
              onChange={(v) => handleColorChange('tertiary', v)}
              testId="tertiary-color-picker"
            />
          </div>

          {/* Color Swatches Preview */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">Current Colors:</span>
            <div className="flex gap-2">
              <div
                className="w-10 h-10 rounded-lg shadow-inner border border-gray-200"
                style={{ backgroundColor: colors.primary }}
                title="Primary"
              />
              <div
                className="w-10 h-10 rounded-lg shadow-inner border border-gray-200"
                style={{ backgroundColor: colors.secondary }}
                title="Secondary"
              />
              {colors.tertiary && (
                <div
                  className="w-10 h-10 rounded-lg shadow-inner border border-gray-200"
                  style={{ backgroundColor: colors.tertiary }}
                  title="Tertiary"
                />
              )}
            </div>
            {colors.origin_country && presets?.[colors.origin_country] && (
              <span className="text-sm text-gray-500 ml-auto">
                Based on: {presets[colors.origin_country].name}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleReset}
              className="text-gray-600"
              data-testid="reset-colors-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Default
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              data-testid="save-colors-btn"
            >
              {saving ? (
                'Saving...'
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Colors
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Export Card */}
      <DataExportCard familyId={familyId} familyName={family?.name} />
    </div>
  );
}

export default SettingsTab;
